import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import CountUp from 'react-countup';
import { 
  FaBook, FaChalkboardTeacher, FaBuilding, FaShieldAlt, FaGraduationCap,
  FaStar, FaTrophy, FaUsers, FaClipboardCheck, FaArrowRight,
  FaPhone, FaEnvelope, FaMapMarkerAlt, FaCalendarAlt, FaClock, FaChevronRight,
  FaPencilAlt, FaAward, FaFlask, FaPalette, FaImages
} from 'react-icons/fa';
import { resolvePublicMediaUrl } from './utils/mediaUrl';
import { DEFAULT_SCHOOL, useSiteSettings } from './context/SiteSettingsContext';

const EnhancedHomePage = () => {
  const { school, whatsapp } = useSiteSettings();
  const autoSessionLabel = (() => {
    const y = new Date().getFullYear();
    const yy = String(y + 1).slice(-2);
    return `${y}-${yy}`;
  })();
  const sessionLabel = (school?.ADMISSIONS_SESSION_LABEL || '').trim() || autoSessionLabel;
  const achievementBadgesRaw = Array.isArray(school?.ACHIEVEMENTS_ITEMS) ? school.ACHIEVEMENTS_ITEMS : [];
  const achievementBadges =
    achievementBadgesRaw.length > 0 ? achievementBadgesRaw : DEFAULT_SCHOOL.ACHIEVEMENTS_ITEMS;

  const statsItemsRaw = Array.isArray(school?.HOME_STATS_ITEMS) ? school.HOME_STATS_ITEMS : [];
  const statsItems = statsItemsRaw.length > 0 ? statsItemsRaw : DEFAULT_SCHOOL.HOME_STATS_ITEMS;
  const statsIconByKey = {
    students: FaUsers,
    teachers: FaChalkboardTeacher,
    years: FaStar,
    result: FaClipboardCheck,
  };
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/news`)
      .then(res => {
        if (!res.ok) throw new Error('Network response was not ok');
        return res.json();
      })
      .then(data => {
        console.log('News data loaded:', data.length, 'items');
        setNews(data.slice(0, 3));
      })
      .catch(err => console.error('Error fetching news:', err.message));
  }, []);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/events`)
      .then(res => {
        if (!res.ok) throw new Error('Network response was not ok');
        return res.json();
      })
      .then(data => {
        console.log('Events data loaded:', data.length, 'items');
        const normalized = (Array.isArray(data) ? data : [])
          .map((e) => ({
            ...e,
            // Backend returns `startDate`; older UI used `date`.
            date: e?.date || e?.startDate || e?.start_date || null,
          }))
          .slice(0, 3);
        setEvents(normalized);
      })
      .catch(err => console.error('Error fetching events:', err.message));
  }, []);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        console.log('🏛️ Fetching gallery images from /api/gallery...');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/gallery`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          // Add timeout
          signal: AbortSignal.timeout(10000)
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('✅ Gallery data loaded:', data.length, 'items');
        
        if (data.length === 0) {
          console.warn('⚠️ No gallery images found in database');
        }
        
        setGalleryImages(data.slice(0, 6));
      } catch (error) {
        console.error('❌ Error fetching gallery:', error.message);
        console.error('   Full error:', error);
        setGalleryImages([]);
      }
    };
    
    fetchGallery();
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0, transition: { duration: 0.6 } },
    viewport: { once: true }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.2 } }
  };

  const cardHover = { scale: 1.05, boxShadow: '0 20px 40px rgba(0,0,0,0.15)', transition: { duration: 0.3 } };

  const quickContactItems = useMemo(() => {
    const waDigits = String(whatsapp.WHATSAPP_NUMBER || '').replace(/\D/g, '');
    const phoneDigits = String(school.SCHOOL_PHONE || '').replace(/\D/g, '');
    const tel =
      waDigits.length > 0
        ? `tel:+${waDigits}`
        : phoneDigits.length >= 10
          ? `tel:+91${phoneDigits.slice(-10)}`
          : `tel:${school.SCHOOL_PHONE}`;
    return [
      { icon: FaPhone, title: 'Call Us', content: school.SCHOOL_PHONE, link: tel },
      { icon: FaEnvelope, title: 'Email Us', content: school.SCHOOL_EMAIL_INFO, link: `mailto:${school.SCHOOL_EMAIL_INFO}` },
      { icon: FaMapMarkerAlt, title: 'Visit Us', content: school.SCHOOL_ADDRESS, link: '/contact' },
    ];
  }, [school, whatsapp]);

  return (
    <div className="overflow-x-hidden bg-white">
      {/* Scroll Progress Bar */}
      <motion.div className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-accent to-pink-600 origin-left z-50" style={{ scaleX }} />
      
      {/* HERO SECTION */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900">
        <motion.div className="absolute inset-0 opacity-30" animate={{ background: ['radial-gradient(circle at 20% 50%, rgba(255,255,255,0.15) 0%, transparent 50%)', 'radial-gradient(circle at 80% 50%, rgba(255,255,255,0.15) 0%, transparent 50%)', 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.15) 0%, transparent 50%)'] }} transition={{ duration: 10, repeat: Infinity }} />

        {/* Parallax Floating Icons */}
        {[FaBook, FaPencilAlt, FaAward, FaStar, FaFlask, FaPalette].map((Icon, i) => (
          <motion.div key={i} className="absolute text-7xl text-white/10" style={{ left: `${15 + i * 15}%`, top: `${20 + (i % 3) * 25}%` }} animate={{ y: [-30, 30, -30], rotate: [0, 360, 0], scale: [1, 1.2, 1] }} transition={{ duration: 8 + i, repeat: Infinity, ease: "easeInOut" }}>
            <Icon />
          </motion.div>
        ))}

        <motion.div className="relative z-10 text-center text-white px-4 max-w-5xl mx-auto" initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.3 }}>
          <motion.img src={resolvePublicMediaUrl(school.SCHOOL_LOGO_URL) || '/images/logo.png'} alt="" className="w-32 h-32 mx-auto mb-6 object-contain drop-shadow-2xl" animate={{ rotate: 360 }} transition={{ duration: 20, repeat: Infinity, ease: "linear" }} />
          <motion.h1 className="text-5xl md:text-7xl lg:text-8xl font-bold font-display mb-6" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5 }}>{school.SCHOOL_NAME}</motion.h1>
          <motion.p className="text-xl md:text-2xl lg:text-3xl mb-4 text-blue-100" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7 }}>{school.SCHOOL_TAGLINE}</motion.p>
          <motion.p className="text-lg md:text-xl mb-8 text-blue-200 max-w-3xl mx-auto" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.9 }}>{school.SCHOOL_HERO_SUBLINE}</motion.p>
          <motion.div className="flex flex-wrap justify-center gap-4" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.1 }}>
            <motion.div whileHover={{ scale: 1.05, boxShadow: '0 20px 60px rgba(236, 72, 153, 0.5)' }} whileTap={{ scale: 0.95 }}><Link to="/admissions" className="inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r from-accent to-pink-600 text-white text-lg font-semibold rounded-full hover:shadow-lg hover:shadow-accent/30 transition-all relative overflow-hidden group"><span className="absolute inset-0 bg-white/20 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></span>Apply Now <FaArrowRight /></Link></motion.div>
            <motion.div whileHover={{ scale: 1.05, boxShadow: '0 20px 60px rgba(13, 71, 161, 0.4)' }} whileTap={{ scale: 0.95 }}><Link to="/about" className="inline-flex items-center gap-2 px-10 py-4 bg-white text-blue-900 text-lg font-semibold rounded-full border-2 border-blue-900 hover:bg-blue-50 transition-all relative overflow-hidden group"><span className="absolute inset-0 bg-blue-900/10 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></span>Explore School <FaArrowRight /></Link></motion.div>
            <motion.div whileHover={{ scale: 1.05, boxShadow: '0 20px 60px rgba(13, 71, 161, 0.4)' }} whileTap={{ scale: 0.95 }}><Link to="/contact" className="inline-flex items-center gap-2 px-10 py-4 bg-white text-blue-900 text-lg font-semibold rounded-full border-2 border-blue-900 hover:bg-blue-50 transition-all relative overflow-hidden group"><span className="absolute inset-0 bg-blue-900/10 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></span>Contact Us <FaArrowRight /></Link></motion.div>
          </motion.div>
        </motion.div>

        <motion.div className="absolute bottom-10 left-1/2 transform -translate-x-1/2" animate={{ y: [0, 10, 0] }} transition={{ duration: 2, repeat: Infinity }}>
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2"><motion.div className="w-1 h-3 bg-white rounded-full" animate={{ y: [0, 12, 0] }} transition={{ duration: 2, repeat: Infinity }} /></div>
        </motion.div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-20 bg-gradient-to-br from-white via-blue-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4 font-display">Why Choose Vidya Vatika?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Experience excellence in education</p>
            <motion.div className="h-1 w-24 bg-gradient-to-r from-blue-900 to-accent mt-6 mx-auto" initial={{ width: 0 }} whileInView={{ width: 96 }} viewport={{ once: true }} transition={{ duration: 0.8 }} />
          </motion.div>
          <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6" variants={staggerContainer} initial="hidden" whileInView="show" viewport={{ once: true }}>
            {[{ icon: FaBook, title: 'CBSE Affiliated', color: 'from-blue-500 to-blue-600' }, { icon: FaChalkboardTeacher, title: 'Experienced Faculty', color: 'from-accent to-pink-600' }, { icon: FaBuilding, title: 'Modern Infrastructure', color: 'from-green-500 to-green-600' }, { icon: FaShieldAlt, title: 'Safe Campus', color: 'from-purple-500 to-purple-600' }, { icon: FaGraduationCap, title: 'Smart Classrooms', color: 'from-orange-500 to-orange-600' }].map((item, index) => (
              <motion.div key={index} variants={fadeInUp} whileHover={cardHover} className="bg-white rounded-2xl shadow-lg p-8 text-center cursor-pointer">
                <div className={`w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br ${item.color} flex items-center justify-center`}><item.icon className="text-3xl text-white" /></div>
                <h3 className="text-xl font-bold text-blue-900">{item.title}</h3>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ACADEMIC PROGRAMS */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4 font-display">Academic Programs</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Comprehensive curriculum for holistic development</p>
            <motion.div className="h-1 w-24 bg-gradient-to-r from-blue-900 to-accent mt-6 mx-auto" initial={{ width: 0 }} whileInView={{ width: 96 }} viewport={{ once: true }} transition={{ duration: 0.8 }} />
          </motion.div>
          <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" variants={staggerContainer} initial="hidden" whileInView="show" viewport={{ once: true }}>
            {[{ title: 'Pre Primary', desc: 'Nurturing young minds' }, { title: 'Primary', desc: 'Building foundations' }, { title: 'Middle School', desc: 'Developing skills' }, { title: 'Senior Secondary', desc: 'Preparing for future' }].map((program, index) => (
              <motion.div key={index} variants={fadeInUp} whileHover={cardHover} className="group relative overflow-hidden rounded-2xl shadow-lg cursor-pointer h-80 bg-gradient-to-br from-blue-900 to-blue-700">
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-all" />
                <div className="absolute inset-0 flex items-center justify-center p-6">
                  <div className="text-center text-white"><h3 className="text-2xl font-bold mb-3">{program.title}</h3><p className="text-blue-100">{program.desc}</p></div>
                </div>
              </motion.div>
            ))}
          </motion.div>
          <motion.div className="text-center mt-12" {...fadeInUp}><Link to="/academics" className="inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r from-blue-900 to-blue-700 text-white text-lg font-semibold rounded-full hover:shadow-lg hover:shadow-blue-900/30 transition-all">Explore Academics <FaArrowRight /></Link></motion.div>
        </div>
      </section>

      {/* FACILITIES */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="container mx-auto px-4">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4 font-display">World-Class Facilities</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">State-of-the-art infrastructure</p>
            <motion.div className="h-1 w-24 bg-gradient-to-r from-blue-900 to-accent mt-6 mx-auto" initial={{ width: 0 }} whileInView={{ width: 96 }} viewport={{ once: true }} transition={{ duration: 0.8 }} />
          </motion.div>
          <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" variants={staggerContainer} initial="hidden" whileInView="show" viewport={{ once: true }}>
            {[{ icon: '🏫', title: 'Smart Classes', desc: 'Interactive digital classrooms' }, { icon: '📚', title: 'Digital Library', desc: 'Extensive resources' }, { icon: '🔬', title: 'Science Labs', desc: 'Fully equipped labs' }, { icon: '💻', title: 'Computer Labs', desc: 'Latest technology' }, { icon: '⚽', title: 'Sports Complex', desc: 'Indoor & outdoor' }, { icon: '🚌', title: 'Transport', desc: 'Safe bus service' }].map((facility, index) => (
              <motion.div key={index} variants={fadeInUp} whileHover={cardHover} className="bg-white rounded-2xl shadow-lg p-6">
                <div className="text-5xl mb-4">{facility.icon}</div>
                <h3 className="text-xl font-bold text-blue-900 mb-2">{facility.title}</h3>
                <p className="text-gray-600">{facility.desc}</p>
              </motion.div>
            ))}
          </motion.div>
          <motion.div className="text-center mt-12" {...fadeInUp}>
            <Link 
              to="/about#infrastructure" 
              onClick={(e) => {
                e.preventDefault();
                window.location.href = '/about#infrastructure';
              }}
              className="inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r from-blue-900 to-blue-700 text-white text-lg font-semibold rounded-full hover:shadow-lg hover:shadow-blue-900/30 transition-all"
            >
              View All Facilities <FaArrowRight />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* LATEST NEWS */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4 font-display">Latest News & Updates</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Stay updated with our achievements</p>
            <motion.div className="h-1 w-24 bg-gradient-to-r from-blue-900 to-accent mt-6 mx-auto" initial={{ width: 0 }} whileInView={{ width: 96 }} viewport={{ once: true }} transition={{ duration: 0.8 }} />
          </motion.div>
          <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-8" variants={staggerContainer} initial="hidden" whileInView="show" viewport={{ once: true }}>
            {news.map((item, index) => (
              <motion.article key={index} variants={fadeInUp} whileHover={cardHover} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                {(item.imageUrl || item.image_url) && (
                  <div className="h-48 overflow-hidden">
                    <img
                      src={resolvePublicMediaUrl(item.imageUrl || item.image_url)}
                      alt={item.title}
                      className="w-full h-full object-cover transform hover:scale-110 transition-transform duration-500"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-3"><FaCalendarAlt /><span>{new Date(item.date).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}</span></div>
                  <h3 className="text-xl font-bold text-blue-900 mb-3 line-clamp-2">{item.title}</h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">{item.description}</p>
                </div>
              </motion.article>
            ))}
          </motion.div>
          <motion.div className="text-center mt-12" {...fadeInUp}>
            <Link 
              to="/news" 
              onClick={() => window.scrollTo(0, 0)}
              className="inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r from-blue-900 to-blue-700 text-white text-lg font-semibold rounded-full hover:shadow-lg hover:shadow-blue-900/30 transition-all"
            >
              View All News <FaArrowRight />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* UPCOMING EVENTS */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white">
        <div className="container mx-auto px-4">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 font-display">Upcoming Events</h2>
            <p className="text-lg text-blue-100 max-w-2xl mx-auto">Join our exciting activities</p>
            <motion.div className="h-1 w-24 bg-white/30 mt-6 mx-auto" initial={{ width: 0 }} whileInView={{ width: 96 }} viewport={{ once: true }} transition={{ duration: 0.8 }} />
          </motion.div>
          <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-8" variants={staggerContainer} initial="hidden" whileInView="show" viewport={{ once: true }}>
            {events.map((event, index) => (
              <motion.div key={index} variants={fadeInUp} whileHover={cardHover} className="bg-white/10 backdrop-blur-md rounded-2xl shadow-lg overflow-hidden">
                {event.imageUrl ? (
                  <img
                    src={resolvePublicMediaUrl(event.imageUrl)}
                    alt=""
                    className="w-full h-44 object-cover"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : null}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="bg-accent text-white px-4 py-2 rounded-full text-sm font-semibold">
                      {event.date ? new Date(event.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—'}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-3 line-clamp-2">{event.title}</h3>
                  <p className="text-blue-100 mb-4 line-clamp-3">{event.description}</p>
                  <div className="flex items-center gap-2 text-sm text-blue-200">
                    <FaClock />
                    <span>
                      {event.date ? new Date(event.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
          <motion.div className="text-center mt-12" {...fadeInUp}>
            <Link 
              to="/events" 
              onClick={() => window.scrollTo(0, 0)}
              className="inline-flex items-center gap-2 px-10 py-4 bg-white text-blue-900 text-lg font-semibold rounded-full hover:bg-blue-50 transition-all"
            >
              View All Events <FaArrowRight />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* GALLERY PREVIEW */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4 font-display">Campus Gallery</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Explore our vibrant community</p>
            <motion.div className="h-1 w-24 bg-gradient-to-r from-blue-900 to-accent mt-6 mx-auto" initial={{ width: 0 }} whileInView={{ width: 96 }} viewport={{ once: true }} transition={{ duration: 0.8 }} />
          </motion.div>
          <motion.div className="grid grid-cols-2 md:grid-cols-3 gap-4" variants={staggerContainer} initial="hidden" whileInView="show" viewport={{ once: true }}>
            {galleryImages.length === 0 ? (
              // Show placeholder if no images
              [...Array(6)].map((_, index) => (
                <motion.div key={index} variants={fadeInUp} className={`rounded-2xl overflow-hidden shadow-lg bg-gradient-to-br from-blue-100 to-blue-50 ${index === 0 ? 'col-span-2 row-span-2' : ''}`}>
                  <div className="h-full min-h-[200px] flex items-center justify-center">
                    <div className="text-center p-4">
                      <FaImages className="text-5xl text-blue-300 mx-auto mb-3" />
                      <p className="text-gray-400 text-sm">No Image</p>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              galleryImages.map((image, index) => (
                <motion.div key={index} variants={fadeInUp} whileHover={cardHover} className={`rounded-2xl overflow-hidden shadow-lg cursor-pointer ${index === 0 ? 'col-span-2 row-span-2' : ''}`}>
                  <div className="relative h-full min-h-[200px] overflow-hidden">
                    <img 
                      src={resolvePublicMediaUrl(image.imageUrl)} 
                      alt={image.title || `Gallery ${index + 1}`} 
                      className="w-full h-full object-cover transform hover:scale-110 transition-transform duration-500" 
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZTNlOGYxIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzljYTVhNSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIE5vdCBGb3VuZDwvdGV4dD48L3N2Zz4=';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </motion.div>
              ))
            )}
          </motion.div>
          <motion.div className="text-center mt-12" {...fadeInUp}>
            <Link 
              to="/gallery" 
              onClick={() => window.scrollTo(0, 0)}
              className="inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r from-blue-900 to-blue-700 text-white text-lg font-semibold rounded-full hover:shadow-lg hover:shadow-blue-900/30 transition-all"
            >
              View Full Gallery <FaArrowRight />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* STATISTICS */}
      <section className="py-20 bg-gradient-to-br from-accent to-pink-600 text-white">
        <div className="container mx-auto px-4">
          {/* Achievement Badges (same design, values dynamic) */}
          {school?.SHOW_ACHIEVEMENTS_SECTION !== false && achievementBadges.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-16"
            >
              <h3 className="text-3xl font-bold text-center mb-8">🏆 Our Achievements & Recognition</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6 max-w-5xl mx-auto">
                {achievementBadges
                  .filter((b) => b?.isActive !== false)
                  .map((badge, index) => (
                    <motion.div
                      key={`${badge?.title || 'badge'}-${index}`}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className="bg-white/10 backdrop-blur-md rounded-xl p-4 text-center hover:bg-white/20 transition-all cursor-pointer shadow-lg hover:shadow-xl"
                    >
                      <div className="text-4xl mb-2">{badge?.icon || '🏆'}</div>
                      <h4 className="font-bold text-sm mb-1">{badge?.title || ''}</h4>
                      <p className="text-xs opacity-80">{badge?.subtitle || badge?.desc || ''}</p>
                    </motion.div>
                  ))}
              </div>
            </motion.div>
          )}

          {/* Stats Counters */}
          {school?.SHOW_HOME_STATS !== false && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {statsItems
                .filter((s) => s?.isActive !== false)
                .map((stat, index) => {
                  const Icon = statsIconByKey[stat?.key] || FaStar;
                  return (
                    <motion.div
                      key={`${stat?.key || 'stat'}-${index}`}
                      initial={{ opacity: 0, scale: 0.5 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="text-center"
                    >
                      <Icon className="text-5xl mx-auto mb-4 opacity-80" />
                      <div className="text-5xl md:text-6xl font-bold mb-2">
                        <CountUp end={Number(stat?.number || 0)} duration={2} suffix={stat?.suffix || ''} />
                      </div>
                      <p className="text-lg opacity-90">{stat?.label || ''}</p>
                    </motion.div>
                  );
                })}
            </div>
          )}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 bg-gradient-to-br from-white via-blue-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4 font-display">What Our Community Says</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Hear from students and parents</p>
            <motion.div className="h-1 w-24 bg-gradient-to-r from-blue-900 to-accent mt-6 mx-auto" initial={{ width: 0 }} whileInView={{ width: 96 }} viewport={{ once: true }} transition={{ duration: 0.8 }} />
          </motion.div>
          <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" variants={staggerContainer} initial="hidden" whileInView="show" viewport={{ once: true }}>
            {[{ name: 'Parent', role: 'Class 10', text: 'Excellent faculty and infrastructure. My child has grown tremendously.' }, { name: 'Student', role: 'Class 12', text: 'Best school in Panipat! The teachers are very supportive.' }, { name: 'Alumni', role: 'Batch 2020', text: 'The values and education I received here shaped my career.' }].map((testimonial, index) => (
              <motion.div key={index} variants={fadeInUp} whileHover={cardHover} className="bg-white rounded-2xl shadow-lg p-8">
                <div className="flex items-center mb-4">{[...Array(5)].map((_, i) => (<FaStar key={i} className="text-accent" />))}</div>
                <p className="text-gray-700 italic mb-6">"{testimonial.text}"</p>
                <div><p className="font-bold text-blue-900">{testimonial.name}</p><p className="text-sm text-gray-500">{testimonial.role}</p></div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ADMISSIONS CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <h2 className="text-5xl md:text-6xl font-bold mb-6">Admissions Open {sessionLabel}</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">Give your child the gift of quality education. Join the Vidya Vatika family today!</p>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}><Link to="/admissions" className="inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r from-accent to-pink-600 text-white text-lg font-semibold rounded-full hover:shadow-lg hover:shadow-accent/30 transition-all">Apply Now <FaArrowRight /></Link></motion.div>
          </motion.div>
        </div>
      </section>

      {/* QUICK CONTACT */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4 font-display">Get In Touch</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">We'd love to hear from you</p>
            <motion.div className="h-1 w-24 bg-gradient-to-r from-blue-900 to-accent mt-6 mx-auto" initial={{ width: 0 }} whileInView={{ width: 96 }} viewport={{ once: true }} transition={{ duration: 0.8 }} />
          </motion.div>
          <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-8" variants={staggerContainer} initial="hidden" whileInView="show" viewport={{ once: true }}>
            {quickContactItems.map((contact, index) => (
              <motion.a key={index} href={contact.link} variants={fadeInUp} whileHover={cardHover} className="bg-gradient-to-br from-blue-50 to-white rounded-2xl shadow-lg p-8 text-center block">
                <div className="flex justify-center mb-4">
                  <contact.icon className="text-5xl text-blue-900" />
                </div>
                <h3 className="text-xl font-bold text-blue-900 mb-2">{contact.title}</h3>
                <p className="text-gray-600">{contact.content}</p>
              </motion.a>
            ))}
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default EnhancedHomePage;
